import React, { useEffect, useState, useMemo, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { Merchant } from '../../types';
import type { MerchantTier, TierAssignment, MerchantTierDetail } from '../../types';
import { fmtBaht, fmtNum } from '../../utils/formatMoney';
import { toast } from 'react-toastify';
import { toastValidationError } from '../../utils/toastValidationError';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faPlus, faXmark, faLock, faBan, faCircleCheck,
  faTriangleExclamation, faWallet, faMoneyBillTransfer, faEdit,
  faSearch, faUserCheck, faHandshake, faLinkSlash, faArrowRight, faVault, faSnowflake, faRotate,
  faLayerGroup, faThumbtack,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { CopyField, CredentialsCopyBlock } from '../../components/shared';
import { swalConfirm } from '../../components/shared/swalConfirm';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';
import TelegramTopicField from '../../components/TelegramTopicField';

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';
const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

const MERCHANT_STATUS_MAP: Record<string, StatusDef> = {
  active:   { bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',  icon: faCircleCheck, label: 'Active'   },
  inactive: { bg: 'bg-red-100 dark:bg-red-900/30',    text: 'text-red-600 dark:text-red-400',      icon: faBan,         label: 'Inactive' },
};

interface MerchantWithWallet extends Merchant {
  wallet?: {
    id: string;
    balance: number;
    payout_balance: number;
    total_credit: number;
    total_debit: number;
    total_payout_credit: number;
    total_payout_debit: number;
    safe_balance: number;
    total_safe_credit: number;
    total_safe_debit: number;
  } | null;
  reseller_id?: string | null;
  resellers?: {
    id: string;
    name: string;
    fee_rate: number;
  } | null;
  telegram_chat_id?: string | null;
  telegram_bot_token?: string | null;
  telegram_enabled?: boolean | null;
  telegram_topic_slip_id?: number | null;
}

interface ResellerOption {
  id: string;
  name: string;
  fee_rate: number;
  is_active: boolean;
  merchant_count: number;
}

type StatusFilter = 'all' | 'active' | 'inactive';

const MerchantsManagementPage: React.FC = () => {
  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isAdminEmployee = adminUser.type === 'admin_employee';
  const permissions = adminUser.permissions || {};
  const canCreateMerchants = !isAdminEmployee || permissions.create_merchants === true;
  const canEditMerchants = !isAdminEmployee || permissions.edit_merchants === true;
  const canReset2FA = !isAdminEmployee;

  const [merchants, setMerchants] = useState<MerchantWithWallet[]>([]);
  const [totalFrozen, setTotalFrozen] = useState(0);
  // ห้องแนบสลิป Telegram (Topics) — false = tenant ยังไม่ลง migration 023 → ซ่อนช่องและไม่ส่ง key
  const [telegramTopicSupported, setTelegramTopicSupported] = useState(true);
  const [usdtRate, setUsdtRate] = useState<number | null>(null);
  const [usdtRateLoading, setUsdtRateLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<MerchantWithWallet | null>(null);
  const [tempPassword, setTempPassword] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: '',
    fee_percentage: '2.00',
    payout_fee_percentage: '0.00',
    telegram_chat_id: '',
    telegram_bot_token: '',
    telegram_enabled: false
  });
  const [editFormData, setEditFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: '',
    fee_percentage: '',
    payout_fee_percentage: '',
    telegram_chat_id: '',
    telegram_bot_token: '',
    telegram_enabled: false,
    telegram_topic_slip_id: '',
    usdt_address: '',
    usdt_network: 'TRC20',
    response_qr_enabled: true,
    response_payment_url_enabled: true,
    p2p_deposit_enabled: false,
    p2p_fee_payin_pct: '',
    p2p_fee_payout_pct: '',
    login_otp_method: 'totp' as 'totp' | 'email'
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTarget, setAssignTarget] = useState<MerchantWithWallet | null>(null);
  const [resellersList, setResellersList] = useState<ResellerOption[]>([]);
  const [selectedResellerId, setSelectedResellerId] = useState('');
  const [assignFee, setAssignFee] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  // ⭐ ระดับร้านค้า — GET /merchants ไม่มีฟิลด์ tier; โหลดแยกจาก /admin/merchant-tiers (+ /assignments แบบ slim)
  const [tiers, setTiers] = useState<MerchantTier[]>([]);
  const [tiersSupported, setTiersSupported] = useState(false);
  const [assignments, setAssignments] = useState<TierAssignment[]>([]);
  const [tierDetail, setTierDetail] = useState<MerchantTierDetail | null>(null);
  const [tierDetailLoading, setTierDetailLoading] = useState(false);
  const [tierOverrideValue, setTierOverrideValue] = useState('');
  const [tierSaving, setTierSaving] = useState(false);
  const [tierRecomputing, setTierRecomputing] = useState(false);
  // ร้านที่ modal แก้ไขเปิดอยู่ตอนนี้ — กัน response ของร้าน A ที่มาช้าทับข้อมูลใน modal ของร้าน B
  const editingMerchantIdRef = useRef<string | null>(null);
  const tierDetailReqSeqRef = useRef(0);
  useEffect(() => {
    // ปิด modal (ทุกทาง) = ไม่มีร้านที่กำลังแก้ → response ที่ค้างอยู่ถูกทิ้งทั้งหมด
    if (!showEditModal) editingMerchantIdRef.current = null;
  }, [showEditModal]);

  useEffect(() => {
    loadPage();
    loadUsdtRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * โหลดหน้า (ครั้งแรก + ปุ่ม Refresh): ยิง /merchants และ /admin/merchant-tiers?include=assignments พร้อมกัน
   * แล้วคง loading ของตารางไว้จนครบทั้งคู่ → render แรกมี badge "ระดับ" มาพร้อมแถว ไม่โผล่ทีหลัง
   * ใช้ allSettled — ฝั่ง tier พัง (จัดการ error ในตัวเองแล้ว) ต้องไม่กันตารางแสดง
   */
  const loadPage = async () => {
    setLoading(true);
    try {
      await Promise.allSettled([fetchMerchants(), loadTierData()]);
    } finally {
      setLoading(false);
    }
  };

  // ใช้ตอน refresh หลังบันทึกระดับ/คำนวณใหม่ใน modal (ต้องการแค่ assignments — ไม่ต้องดึง tiers ซ้ำ)
  const loadAssignments = async () => {
    try {
      const res = await merchantApi.get('/admin/merchant-tiers/assignments');
      if (res.data?.supported === false) {
        setTiersSupported(false);
        setAssignments([]);
        return;
      }
      setAssignments(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'โหลดระดับของร้านค้าไม่สำเร็จ');
    }
  };

  /**
   * tiers + assignments ในนัดเดียว (GET /admin/merchant-tiers?include=assignments)
   * - supported:false → ล้างทั้งคู่ (คอลัมน์ระดับแสดง '-')
   * - assignments:null (RPC assignments พังแต่ tiers มา) → คง assignments เดิม + toast `warning` จาก backend
   * - ไม่มี key assignments เลย = backend เก่าที่ยังไม่รู้จัก include (deploy frontend ก่อน / tenant ยังไม่อัปเดต)
   *   → fallback ยิง GET /assignments แยกเหมือนเดิม กัน badge เป็น '-' เงียบ ๆ (backend ใหม่ส่ง key เสมอ → path หลักยังนัดเดียว)
   * - request พัง → tiersSupported=false ให้ badge เป็น '-' แต่ตารางยังแสดงปกติ
   */
  const loadTierData = async () => {
    try {
      const res = await merchantApi.get('/admin/merchant-tiers', { params: { include: 'assignments' } });
      const supported = res.data?.supported === true;
      setTiersSupported(supported);
      setTiers(supported ? (res.data.tiers || []) : []);
      if (!supported) {
        setAssignments([]);
        return;
      }
      if (!('assignments' in res.data)) {
        await loadAssignments();
        return;
      }
      if (Array.isArray(res.data.assignments)) setAssignments(res.data.assignments);
      if (res.data.warning) toast.warn(res.data.warning);
    } catch (err: any) {
      setTiersSupported(false);
      toast.error(err.response?.data?.message || 'โหลดระดับร้านค้าไม่สำเร็จ');
    }
  };

  const loadTierDetail = async (merchantId: string) => {
    const seq = ++tierDetailReqSeqRef.current;
    // response เก่า (ปิด modal / เปิดร้านอื่นไปแล้ว) → ทิ้ง ห้ามแตะ state
    const isStale = () => seq !== tierDetailReqSeqRef.current || editingMerchantIdRef.current !== merchantId;
    setTierDetailLoading(true);
    try {
      const res = await merchantApi.get(`/admin/merchant-tiers/merchants/${merchantId}`);
      if (isStale()) return;
      if (res.data?.supported === false) {
        setTiersSupported(false);
        setTierDetail(null);
        return;
      }
      const detail: MerchantTierDetail = res.data.data;
      if (detail?.merchant_id !== merchantId) return; // backend ตอบร้านคนละใบ — ไม่ควรเกิด แต่ห้ามเอามาแสดง
      setTierDetail(detail);
      // ⚠️ ห้ามใช้ || '' — '' = อัตโนมัติ; ต้องล้อค่าที่เปิด modal เพื่อส่ง expected_current ตอนบันทึก
      setTierOverrideValue(detail.tier_override_id ?? '');
    } catch (err: any) {
      if (isStale()) return;
      setTierDetail(null);
      toast.error(err.response?.data?.message || 'โหลดข้อมูลระดับร้านค้าไม่สำเร็จ');
    } finally {
      if (!isStale()) setTierDetailLoading(false);
    }
  };

  const tiersById = useMemo(() => new Map(tiers.map((t) => [t.id, t])), [tiers]);
  const assignmentsById = useMemo(() => {
    const map = new Map<string, { tier_id: string | null; tier_override_id: string | null }>();
    assignments.forEach(([merchantId, tierId, overrideId]) => {
      map.set(merchantId, { tier_id: tierId ?? null, tier_override_id: overrideId ?? null });
    });
    return map;
  }, [assignments]);

  const loadUsdtRate = async () => {
    setUsdtRateLoading(true);
    try {
      const res = await merchantApi.get('/admin/freeze/usdt-rate');
      setUsdtRate(res.data.data?.rate || null);
    } catch {
      setUsdtRate(null);
    } finally {
      setUsdtRateLoading(false);
    }
  };

  // ดึงรายชื่อร้านอย่างเดียว ไม่แตะ loading — ให้ผู้เรียก (loadMerchants / loadPage) คุมเอง; จัดการ error ในตัว ไม่ reject
  const fetchMerchants = async () => {
    try {
      const response = await merchantApi.get('/merchants');
      setMerchants(response.data.data);
      setTotalFrozen(response.data.total_frozen || 0);
      setTelegramTopicSupported(response.data.telegram_topic_supported !== false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'ไม่สามารถโหลดข้อมูลได้');
    }
  };

  const loadMerchants = async () => {
    setLoading(true);
    try {
      await fetchMerchants();
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      const response = await merchantApi.post('/merchants', formData);
      setSelectedMerchant(response.data.data);
      setTempPassword(formData.password);
      setShowDetailModal(true);
      toast.success('สร้าง Merchant สำเร็จ!');
      setShowCreateModal(false);
      setFormData({
        email: '',
        password: '',
        name: '',
        company_name: '',
        phone: '',
        fee_percentage: '2.00',
        payout_fee_percentage: '0.00',
        telegram_chat_id: '',
        telegram_bot_token: '',
        telegram_enabled: false
      });
      loadMerchants();
    } catch (error: any) {
      toastValidationError(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    const ok = await swalConfirm({
      title: 'ระงับ Merchant นี้?',
      text: 'Merchant จะไม่สามารถใช้งานได้จนกว่าจะปลดระงับ',
      icon: 'warning',
      confirmText: 'ระงับ',
      confirmColor: '#ef4444',
    });
    if (!ok) return;
    try {
      await merchantApi.post(`/merchants/${id}/deactivate`);
      toast.success('ระงับ Merchant สำเร็จ');
      loadMerchants();
    } catch (error: any) {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  const handleReactivate = async (id: string) => {
    const ok = await swalConfirm({
      title: 'ปลดระงับ Merchant นี้?',
      icon: 'question',
      confirmText: 'ปลดระงับ',
      confirmColor: '#10b981',
    });
    if (!ok) return;
    try {
      await merchantApi.post(`/merchants/${id}/reactivate`);
      toast.success('ปลดระงับ Merchant สำเร็จ');
      loadMerchants();
    } catch (error: any) {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  const handleReset2FA = async (id: string) => {
    const ok = await swalConfirm({
      title: 'Reset 2FA ของ Merchant นี้?',
      text: 'Merchant จะต้องตั้งค่า 2FA ใหม่เมื่อ login ครั้งถัดไป',
      icon: 'warning',
      confirmText: 'Reset 2FA',
      confirmColor: '#f59e0b',
    });
    if (!ok) return;
    try {
      await merchantApi.post(`/2fa/admin/reset/${id}`);
      toast.success('Reset 2FA สำเร็จ');
    } catch (error: any) {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMerchant) return;

    setEditLoading(true);
    try {
      const payload: Record<string, string | boolean | number | null> = {};
      if (editFormData.email) payload.email = editFormData.email;
      if (editFormData.password) payload.password = editFormData.password;
      if (editFormData.name) payload.name = editFormData.name;
      if (editFormData.company_name !== undefined) payload.company_name = editFormData.company_name;
      if (editFormData.phone !== undefined) payload.phone = editFormData.phone;
      if (editFormData.fee_percentage) payload.fee_percentage = editFormData.fee_percentage;
      if (editFormData.payout_fee_percentage) payload.payout_fee_percentage = editFormData.payout_fee_percentage;
      if (editFormData.telegram_chat_id !== undefined) payload.telegram_chat_id = editFormData.telegram_chat_id;
      if (editFormData.telegram_bot_token !== undefined) payload.telegram_bot_token = editFormData.telegram_bot_token;
      payload.telegram_enabled = editFormData.telegram_enabled;
      // ห้องแนบสลิป (Topics): ส่งเฉพาะเมื่อ tenant รองรับ — ว่าง = null = รับทุกห้อง
      if (telegramTopicSupported) {
        payload.telegram_topic_slip_id = editFormData.telegram_topic_slip_id === '' ? null : Number(editFormData.telegram_topic_slip_id);
      }
      if (editFormData.usdt_address !== undefined) payload.usdt_address = editFormData.usdt_address;
      if (editFormData.usdt_network) payload.usdt_network = editFormData.usdt_network;

      // 🎛️ API Response toggles - validate at least one is enabled
      if (!editFormData.response_qr_enabled && !editFormData.response_payment_url_enabled) {
        toast.error('ต้องเปิด API Response อย่างน้อย 1 อย่าง (QR Code หรือ Payment URL)');
        setEditLoading(false);
        return;
      }
      payload.response_qr_enabled = editFormData.response_qr_enabled;
      payload.response_payment_url_enabled = editFormData.response_payment_url_enabled;
      payload.p2p_deposit_enabled = editFormData.p2p_deposit_enabled;
      // 💧 P2P fee: ส่งเสมอ — ช่องว่าง = null = กลับไปใช้ fee ปกติ (fallback)
      payload.p2p_fee_payin_pct = editFormData.p2p_fee_payin_pct === '' ? null : Number(editFormData.p2p_fee_payin_pct);
      payload.p2p_fee_payout_pct = editFormData.p2p_fee_payout_pct === '' ? null : Number(editFormData.p2p_fee_payout_pct);
      payload.login_otp_method = editFormData.login_otp_method;

      await merchantApi.put(`/merchants/${selectedMerchant.id}`, payload);
      toast.success('อัพเดท Merchant สำเร็จ!');
      setShowEditModal(false);
      setSelectedMerchant(null);
      loadMerchants();
    } catch (error: any) {
      toastValidationError(error);
    } finally {
      setEditLoading(false);
    }
  };

  const openEditModal = (merchant: MerchantWithWallet) => {
    setSelectedMerchant(merchant);
    setEditFormData({
      email: merchant.email || '',
      password: '',
      name: merchant.name || '',
      company_name: merchant.company_name || '',
      phone: merchant.phone || '',
      fee_percentage: merchant.fee_percentage?.toString() || '',
      payout_fee_percentage: merchant.payout_fee_percentage?.toString() || '0.00',
      telegram_chat_id: merchant.telegram_chat_id || '',
      telegram_bot_token: merchant.telegram_bot_token || '',
      telegram_enabled: merchant.telegram_enabled || false,
      telegram_topic_slip_id: merchant.telegram_topic_slip_id != null ? String(merchant.telegram_topic_slip_id) : '',
      usdt_address: (merchant as any).usdt_address || '',
      usdt_network: (merchant as any).usdt_network || 'TRC20',
      response_qr_enabled: merchant.response_qr_enabled !== false,
      response_payment_url_enabled: merchant.response_payment_url_enabled !== false,
      p2p_deposit_enabled: merchant.p2p_deposit_enabled === true,
      // ⚠️ ห้ามใช้ || '' — 0% เป็นค่า valid ต้องเช็ค != null เท่านั้น
      p2p_fee_payin_pct: merchant.p2p_fee_payin_pct != null ? String(merchant.p2p_fee_payin_pct) : '',
      p2p_fee_payout_pct: merchant.p2p_fee_payout_pct != null ? String(merchant.p2p_fee_payout_pct) : '',
      login_otp_method: ((merchant as any).login_otp_method || 'totp') as 'totp' | 'email'
    });
    editingMerchantIdRef.current = merchant.id;
    setTierDetail(null);
    setTierOverrideValue('');
    setShowEditModal(true);
    if (tiersSupported) loadTierDetail(merchant.id);
  };

  // ⭐ override ระดับ — endpoint แยกจาก PUT /merchants/:id (ห้ามรวมใน handleEditSubmit); ส่งเฉพาะเมื่อค่าเปลี่ยน
  const handleSaveTierOverride = async () => {
    if (!selectedMerchant || !tierDetail) return;
    const merchantId = selectedMerchant.id;
    // detail ต้องเป็นของร้านที่เปิดอยู่ — ไม่งั้น expected_current จะเป็นของร้านอื่น (null ตรงกันก็ผ่าน stale check ได้)
    if (tierDetail.merchant_id !== merchantId) {
      toast.error('ข้อมูลระดับไม่ตรงกับร้านที่เปิดอยู่ กรุณาปิดแล้วเปิดใหม่');
      return;
    }
    const current = tierDetail.tier_override_id ?? '';
    if (tierOverrideValue === current) return;
    setTierSaving(true);
    try {
      const res = await merchantApi.put(`/merchants/${merchantId}/tier-override`, {
        tier_override_id: tierOverrideValue === '' ? null : tierOverrideValue,
        expected_current: tierDetail.tier_override_id ?? null,
      });
      toast.success(res.data?.data?.changed === false ? 'ระดับร้านค้าไม่เปลี่ยนแปลง' : 'บันทึกระดับร้านค้าสำเร็จ');
      await Promise.all([loadTierDetail(merchantId), loadAssignments()]);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'บันทึกระดับร้านค้าไม่สำเร็จ');
      // 409 stale → โหลดค่าปัจจุบันให้ผู้ใช้เห็นก่อนลองใหม่
      if (err.response?.status === 409) loadTierDetail(merchantId);
    } finally {
      setTierSaving(false);
    }
  };

  const handleRecomputeMerchantTier = async () => {
    if (!selectedMerchant) return;
    const merchantId = selectedMerchant.id;
    setTierRecomputing(true);
    try {
      const res = await merchantApi.post('/admin/merchant-tiers/recompute', { merchant_id: merchantId });
      const result = res.data?.data;
      // 200 แต่ ok:false (เช่น RPC_MISSING) = ไม่ได้คำนวณ — ห้ามรายงานว่าสำเร็จ
      if (result?.ok === false) {
        toast.error(res.data?.message || `คำนวณระดับใหม่ไม่สำเร็จ (${result.code || 'UNKNOWN'})`);
        return;
      }
      const changed = Number(result?.changed ?? 0);
      toast.success(changed > 0 ? 'คำนวณใหม่แล้ว — ระดับเปลี่ยน' : 'คำนวณใหม่แล้ว — ระดับคงเดิม');
      await Promise.all([loadTierDetail(merchantId), loadAssignments()]);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'คำนวณระดับใหม่ไม่สำเร็จ');
    } finally {
      setTierRecomputing(false);
    }
  };

  const loadResellers = async () => {
    try {
      const response = await merchantApi.get('/admin/resellers');
      setResellersList(response.data.data || []);
    } catch {
      toast.error('โหลดรายชื่อ Reseller ไม่ได้');
    }
  };

  const openAssignModal = (merchant: MerchantWithWallet) => {
    setAssignTarget(merchant);
    setSelectedResellerId(merchant.reseller_id || '');
    setAssignFee(merchant.fee_percentage?.toString() || '');
    setShowAssignModal(true);
    loadResellers();
  };

  const handleAssign = async () => {
    if (!assignTarget || !selectedResellerId) return;
    setAssignLoading(true);
    try {
      await merchantApi.post('/admin/resellers/assign-merchant', {
        merchant_id: assignTarget.id,
        reseller_id: selectedResellerId,
        fee_percentage: assignFee ? parseFloat(assignFee) : undefined
      });
      toast.success('ย้าย Merchant เข้า Reseller สำเร็จ!');
      setShowAssignModal(false);
      setAssignTarget(null);
      loadMerchants();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleUnassign = async (merchant: MerchantWithWallet) => {
    const ok = await swalConfirm({
      title: `ถอด "${merchant.name}" ออกจาก Reseller?`,
      text: merchant.resellers?.name ? `Reseller: ${merchant.resellers.name}` : undefined,
      icon: 'warning',
      confirmText: 'ถอดออก',
      confirmColor: '#f97316',
    });
    if (!ok) return;
    try {
      await merchantApi.post('/admin/resellers/unassign-merchant', {
        merchant_id: merchant.id
      });
      toast.success('ถอด Merchant ออกจาก Reseller สำเร็จ');
      loadMerchants();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  // Computed: selected reseller info for assign modal
  const selectedReseller = resellersList.find(r => r.id === selectedResellerId);
  const assignFeeNum = parseFloat(assignFee) || 0;
  const feeWarning = selectedReseller && assignFeeNum < selectedReseller.fee_rate;
  const commissionPreview = selectedReseller ? Math.max(0, assignFeeNum - selectedReseller.fee_rate) : 0;

  // Filtered merchants
  const filteredMerchants = useMemo(() => {
    let result = merchants;

    if (statusFilter === 'active') {
      result = result.filter(m => m.is_active);
    } else if (statusFilter === 'inactive') {
      result = result.filter(m => !m.is_active);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(m =>
        m.name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.company_name?.toLowerCase().includes(q) ||
        m.phone?.includes(q)
      );
    }

    return result;
  }, [merchants, statusFilter, searchQuery]);

  // Stats
  const activeCount = merchants.filter(m => m.is_active).length;
  const inactiveCount = merchants.filter(m => !m.is_active).length;
  const totalBalance = merchants.reduce((sum, m) => sum + (m.wallet?.balance || 0), 0);
  const totalPayout = merchants.reduce((sum, m) => sum + (m.wallet?.payout_balance || 0), 0);
  const totalSafe = merchants.reduce((sum, m) => sum + (m.wallet?.safe_balance || 0), 0);

  const formatBalance = (val: number) =>
    val.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const columns: Column<MerchantWithWallet>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Merchant',
      sortable: true,
      render: (m) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
               style={{ background: 'var(--theme-primary-gradient)' }}>
            {m.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-[13px] font-semibold text-gray-800 dark:text-slate-100 truncate">{m.name}</p>
              <StatusBadge
                status={m.is_active ? 'active' : 'inactive'}
                statusMap={MERCHANT_STATUS_MAP}
              />
            </div>
            <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate">{m.company_name || 'ไม่ระบุบริษัท'}</p>
            {m.resellers && (
              <p className="text-[10px] text-orange-500 dark:text-orange-400 truncate flex items-center gap-1 mt-0.5">
                <span className="w-1 h-1 rounded-full bg-orange-400"></span>
                Reseller: {m.resellers.name}
              </p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'email',
      header: 'ข้อมูลติดต่อ',
      sortable: true,
      render: (m) => (
        <div className="min-w-0">
          <p className="text-[13px] text-gray-700 dark:text-slate-200 truncate">{m.email}</p>
          <p className="text-[11px] text-gray-400 dark:text-slate-500">{m.phone || '-'}</p>
        </div>
      )
    },
    {
      key: 'fee_percentage',
      header: 'Fee (Payin/Payout)',
      width: '100px',
      sortable: true,
      render: (m) => (
        <div className="text-[12px]">
          <div className="font-medium text-gray-700 dark:text-slate-200">
            P.In: {m.fee_percentage != null ? `${m.fee_percentage}%` : '-'}
          </div>
          <div className="text-gray-500 dark:text-slate-400">
            P.Out: {m.payout_fee_percentage != null ? `${m.payout_fee_percentage}%` : '0%'}
          </div>
          {(m.p2p_deposit_enabled || m.p2p_fee_payin_pct != null || m.p2p_fee_payout_pct != null) && (
            <div className="text-blue-500 dark:text-blue-400">
              {m.p2p_deposit_enabled ? '💧 ' : ''}P2P: {m.p2p_fee_payin_pct != null ? `${m.p2p_fee_payin_pct}%` : '↩'}/{m.p2p_fee_payout_pct != null ? `${m.p2p_fee_payout_pct}%` : '↩'}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'tier',
      header: 'ระดับ',
      width: '110px',
      className: 'whitespace-nowrap',
      render: (m) => {
        if (!tiersSupported) return <span className="text-[12px] text-gray-300 dark:text-slate-600">-</span>;
        const a = assignmentsById.get(m.id);
        const effectiveId = a?.tier_override_id ?? a?.tier_id ?? null;
        if (effectiveId === null) return <span className="text-[12px] text-gray-300 dark:text-slate-600">-</span>;
        const tier = tiersById.get(effectiveId);
        const pinned = a?.tier_override_id !== null && a?.tier_override_id !== undefined;
        // tier ถูกปิดใช้งาน = backend ถือว่าร้านไม่มี tier (pin ค้าง / computed ค้างจนกว่าจะคำนวณใหม่) — อย่าโชว์เหมือน tier ปกติ
        const inactive = tier !== undefined && tier.is_active === false;
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
              inactive
                ? 'bg-gray-50 dark:bg-slate-800 text-gray-400 dark:text-slate-500 ring-1 ring-dashed ring-gray-300 dark:ring-slate-600'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200'
            }`}
            title={
              inactive
                ? 'ระดับนี้ถูกปิดใช้งาน — ร้านไม่มีระดับจนกว่าจะ pin ใหม่/คำนวณใหม่'
                : pinned ? 'pin โดยแอดมิน (override)' : 'คำนวณจากยอดสะสม'
            }
          >
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: inactive ? '#d1d5db' : (tier?.color || '#9ca3af') }} />
            <span className={inactive ? 'line-through' : undefined}>{tier?.name ?? '(ไม่พบ)'}</span>
            {inactive && <span className="text-[9px]">(ปิดใช้งาน)</span>}
            {pinned && <FontAwesomeIcon icon={faThumbtack} className={`text-[9px] ${inactive ? 'text-gray-400' : 'text-amber-500'}`} />}
          </span>
        );
      }
    },
    {
      key: 'wallets',
      header: 'ยอดเงิน',
      render: (m) => (
        <div className="space-y-0.5 min-w-[140px]">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">Main</span>
            <span className="text-[13px] font-semibold text-gray-800 dark:text-slate-100">
              ฿{formatBalance(m.wallet?.balance || 0)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">Payout</span>
            <span className="text-[13px] font-semibold text-gray-800 dark:text-slate-100">
              ฿{formatBalance(m.wallet?.payout_balance || 0)}
            </span>
          </div>
          {(m.wallet?.safe_balance || 0) > 0 && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] text-amber-500 uppercase font-medium">Safe</span>
              <span className="text-[13px] font-semibold text-amber-600 dark:text-amber-400">
                {formatBalance(m.wallet?.safe_balance || 0)} USDT
              </span>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'created_at',
      header: 'สร้างเมื่อ',
      sortable: true,
      width: '90px',
      render: (m) => (
        <span className="text-[12px] text-gray-400 dark:text-slate-500">
          {m.created_at ? new Date(m.created_at).toLocaleDateString('th-TH') : '-'}
        </span>
      )
    },
    {
      key: 'actions',
      header: 'จัดการ',
      width: '160px',
      render: (m) => canEditMerchants ? (
        <div className="flex items-center gap-1 flex-wrap">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openEditModal(m); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
            title="แก้ไข"
          >
            <FontAwesomeIcon icon={faEdit} className="text-[10px]" />
            แก้ไข
          </button>
          {m.reseller_id ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleUnassign(m); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-colors flex items-center gap-1"
              title="ถอด Reseller"
            >
              <FontAwesomeIcon icon={faLinkSlash} className="text-[10px]" />
              ถอด
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openAssignModal(m); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-colors flex items-center gap-1"
              title="ย้ายเข้า Reseller"
            >
              <FontAwesomeIcon icon={faHandshake} className="text-[10px]" />
              Reseller
            </button>
          )}
          {canReset2FA && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleReset2FA(m.id); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors flex items-center gap-1"
              title="Reset 2FA"
            >
              <FontAwesomeIcon icon={faLock} className="text-[10px]" />
              2FA
            </button>
          )}
          {m.is_active ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDeactivate(m.id); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors flex items-center gap-1"
              title="ระงับ"
            >
              <FontAwesomeIcon icon={faBan} className="text-[10px]" />
              ระงับ
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleReactivate(m.id); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors flex items-center gap-1"
              title="ปลดระงับ"
            >
              <FontAwesomeIcon icon={faCircleCheck} className="text-[10px]" />
              ปลดระงับ
            </button>
          )}
        </div>
      ) : null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEditMerchants, canReset2FA, tiersSupported, tiersById, assignmentsById]);

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faUsers,
      iconClassName: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
      label: 'ทั้งหมด',
      value: <span className="text-gray-800 dark:text-slate-100">{merchants.length.toLocaleString()}</span>,
    },
    {
      icon: faUserCheck,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      label: 'ใช้งาน',
      value: <span className="text-emerald-600 dark:text-emerald-400">{activeCount.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ระงับ {inactiveCount}</span>,
    },
    {
      icon: faWallet,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'Main Wallet',
      value: <span className="text-gray-800 dark:text-slate-100">฿{formatBalance(totalBalance)}</span>,
    },
    {
      icon: faMoneyBillTransfer,
      iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: 'Payout Wallet',
      value: <span className="text-gray-800 dark:text-slate-100">฿{formatBalance(totalPayout)}</span>,
    },
    {
      icon: faVault,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'Safe Wallet',
      value: <span className="text-amber-600 dark:text-amber-400">{formatBalance(totalSafe)} USDT</span>,
      subValue: (
        <span className="text-xs text-gray-400 dark:text-slate-500">
          {usdtRateLoading
            ? 'กำลังโหลดเรท…'
            : usdtRate
              ? <>≈ ฿{formatBalance(totalSafe * usdtRate)} <span className="text-gray-300 dark:text-slate-600">·</span> ฿{formatBalance(usdtRate)}/USDT</>
              : 'เรทไม่พร้อมใช้งาน'}
        </span>
      ),
    },
    {
      icon: faSnowflake,
      iconClassName: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400',
      label: 'Freeze',
      value: <span className="text-gray-800 dark:text-slate-100">฿{formatBalance(totalFrozen)}</span>,
    },
  ], [merchants.length, activeCount, inactiveCount, totalBalance, totalPayout, totalSafe, totalFrozen, usdtRate, usdtRateLoading]);

  const handleFilterReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
  };

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faUsers}
        title="Merchants"
        subtitle={`จัดการข้อมูล Merchant ทั้งหมด • ${merchants.length.toLocaleString()} รายการ`}
        actions={
          canCreateMerchants ? (
            <UiButton icon={faPlus} onClick={() => setShowCreateModal(true)}>
              เพิ่ม Merchant
            </UiButton>
          ) : undefined
        }
      />

      <UiStatsGrid cards={statCards} loading={loading} cols={3} />

      <UiFilterPanel
        onSearch={() => {}}
        onReset={handleFilterReset}
        loading={loading}
        actions={
          <button
            type="button"
            onClick={() => loadPage()}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      >
        <div className="flex flex-wrap items-end gap-4 w-fit">
        <div className="w-[280px] shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            ค้นหา
          </label>
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="ค้นหาชื่อ, อีเมล, บริษัท..."
              className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 dark:focus:ring-slate-600 dark:focus:border-slate-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
              >
                <FontAwesomeIcon icon={faXmark} className="text-sm" />
              </button>
            )}
          </div>
        </div>

        <div className="w-[160px] shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className={FILTER_SELECT_CLASS}
          >
            <option value="all">ทั้งหมด ({merchants.length})</option>
            <option value="active">ใช้งาน ({activeCount})</option>
            <option value="inactive">ระงับ ({inactiveCount})</option>
          </select>
        </div>
        </div>
      </UiFilterPanel>

      <DataTable
        data={filteredMerchants}
        columns={columns}
        loading={loading}
        keyExtractor={(m) => m.id}
        emptyMessage={searchQuery || statusFilter !== 'all' ? 'ไม่พบ Merchant ที่ค้นหา' : 'ยังไม่มี Merchant'}
        emptyIcon={<FontAwesomeIcon icon={faUsers} className="text-5xl text-gray-200 dark:text-slate-600" />}
        hoverable
        compact
        pagination={{
          enabled: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          showPageSizeSelector: true,
          showInfo: true,
          showFirstLast: true
        }}
      />

      <UiModal
        open={showCreateModal}
        onClose={() => { if (!createLoading) setShowCreateModal(false); }}
        title="สร้าง Merchant ใหม่"
        icon={faPlus}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
              disabled={createLoading}
              className="flex-1 justify-center"
            >
              ยกเลิก
            </UiButton>
            <UiButton
              loading={createLoading}
              onClick={() => (document.getElementById('merchant-create-form') as HTMLFormElement | null)?.requestSubmit()}
              className="flex-1 justify-center"
            >
              สร้าง Merchant
            </UiButton>
          </div>
        }
      >
        <form id="merchant-create-form" onSubmit={handleCreateSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASS}>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="email@example.com"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>ชื่อ *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="ชื่อ Merchant"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>ชื่อบริษัท</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="บริษัท ABC จำกัด"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>เบอร์โทร</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="0xx-xxx-xxxx"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>ค่าธรรมเนียมฝาก (%) *</label>
                  <input
                    type="number"
                    value={formData.fee_percentage}
                    onChange={(e) => setFormData({ ...formData, fee_percentage: e.target.value })}
                    step="0.01"
                    min="0"
                    max="100"
                    required
                    className={INPUT_CLASS}
                  />
                  <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">Payin Fee เช่น 2.00 = 2%</p>
                </div>
                <div>
                  <label className={LABEL_CLASS}>ค่าธรรมเนียมถอน (%)</label>
                  <input
                    type="number"
                    value={formData.payout_fee_percentage}
                    onChange={(e) => setFormData({ ...formData, payout_fee_percentage: e.target.value })}
                    step="0.01"
                    min="0"
                    max="100"
                    className={INPUT_CLASS}
                  />
                  <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">Payout Fee เช่น 2.00 = 2% (บวกเพิ่มจากยอดถอน)</p>
                </div>
              </div>

              <div className="rounded-xl border border-sky-100 dark:border-sky-800 bg-sky-50/40 dark:bg-sky-900/20 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-sky-100 dark:border-sky-800 bg-sky-50/60 dark:bg-sky-900/30">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-sky-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-sky-700 dark:text-sky-300">Telegram</span>
                    <span className="text-[11px] text-sky-400 dark:text-sky-500">(ไม่บังคับ)</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <span className="text-xs text-gray-400 dark:text-slate-500">{formData.telegram_enabled ? 'เปิด' : 'ปิด'}</span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={formData.telegram_enabled}
                        onChange={(e) => setFormData({ ...formData, telegram_enabled: e.target.checked })}
                        className="sr-only"
                      />
                      <div className={`w-9 h-5 rounded-full transition-colors duration-200 ${formData.telegram_enabled ? 'bg-sky-500' : 'bg-gray-200 dark:bg-slate-600'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${formData.telegram_enabled ? 'translate-x-4' : ''}`} />
                      </div>
                    </div>
                  </label>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className={LABEL_CLASS}>Bot Token</label>
                    <input
                      type="text"
                      value={formData.telegram_bot_token}
                      onChange={(e) => setFormData({ ...formData, telegram_bot_token: e.target.value })}
                      placeholder="1234567890:AAxxxxxxxx..."
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className={LABEL_CLASS}>Chat ID</label>
                    <input
                      type="text"
                      value={formData.telegram_chat_id}
                      onChange={(e) => setFormData({ ...formData, telegram_chat_id: e.target.value })}
                      placeholder="-100xxxxxxxxxx"
                      className={INPUT_CLASS}
                    />
                  </div>
                </div>
              </div>
        </form>
      </UiModal>

      {showDetailModal && selectedMerchant && (() => {
        const loginUrl = process.env.NODE_ENV === 'production'
          ? `${window.location.origin.replace(/^(https?:\/\/)[^.]+\./, '$1merchant.')}/login`
          : `${window.location.origin}/login`;
        const closeDetail = () => { setShowDetailModal(false); setTempPassword(''); };
        return (
          <UiModal
            open={showDetailModal}
            onClose={closeDetail}
            title="Merchant สร้างสำเร็จ!"
            icon={faCircleCheck}
            iconColor="text-emerald-600"
            size="lg"
            scrollable
            footer={
              <UiButton variant="secondary" onClick={closeDetail} className="w-full justify-center">
                ปิด
              </UiButton>
            }
          >
            <div className="text-center -mt-2 mb-2">
              <p className="text-xs text-gray-400 dark:text-slate-500">{selectedMerchant.name}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 ring-1 ring-blue-100 dark:ring-blue-800 space-y-2">
              <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-1">
                ข้อมูลแยกฟิลด์
              </h4>
              <CopyField label="URL Login" value={loginUrl} className="text-sm" />
              <CopyField label="User" value={selectedMerchant.email || ''} className="text-sm" />
              <CopyField label="Password" value={tempPassword || '-'} className="text-sm" />
              <p className="text-[11px] text-gray-500 dark:text-slate-400 italic mt-2">
                หมายเหตุ: Merchant ต้องเข้าไปสร้าง API Key เองในหน้า API Keys หลัง login
              </p>
            </div>
            <div className="bg-white dark:bg-slate-700/50 rounded-xl p-4 ring-1 ring-gray-200 dark:ring-slate-600 space-y-2">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-slate-200 uppercase tracking-wide mb-1">
                ข้อความสำหรับส่งให้ลูกค้า
              </h4>
              <CredentialsCopyBlock
                credentials={{
                  loginUrl,
                  user: selectedMerchant.email || '',
                  password: tempPassword || '',
                  merchantName: selectedMerchant.name,
                }}
              />
            </div>
          </UiModal>
        );
      })()}

      <UiModal
        open={showEditModal}
        onClose={() => { if (!editLoading) setShowEditModal(false); }}
        title={`แก้ไข ${selectedMerchant?.name || ''}`}
        icon={faEdit}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton
              variant="secondary"
              onClick={() => setShowEditModal(false)}
              disabled={editLoading}
              className="flex-1 justify-center"
            >
              ยกเลิก
            </UiButton>
            <UiButton
              loading={editLoading}
              disabled={!editFormData.response_qr_enabled && !editFormData.response_payment_url_enabled}
              onClick={() => (document.getElementById('merchant-edit-form') as HTMLFormElement | null)?.requestSubmit()}
              className="flex-1 justify-center"
            >
              บันทึกการแก้ไข
            </UiButton>
          </div>
        }
      >
        <form id="merchant-edit-form" onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASS}>Email</label>
                  <input
                    type="email"
                    readOnly
                    value={editFormData.email}
                    className={`${INPUT_CLASS} bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-slate-400 cursor-not-allowed`}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Password ใหม่</label>
                  <input
                    type="password"
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    placeholder="เว้นว่างถ้าไม่เปลี่ยน"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>ชื่อ *</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    required
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>ชื่อบริษัท</label>
                  <input
                    type="text"
                    value={editFormData.company_name}
                    onChange={(e) => setEditFormData({ ...editFormData, company_name: e.target.value })}
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>เบอร์โทร</label>
                  <input
                    type="tel"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>ค่าธรรมเนียมฝาก (%)</label>
                  <input
                    type="number"
                    value={editFormData.fee_percentage}
                    onChange={(e) => setEditFormData({ ...editFormData, fee_percentage: e.target.value })}
                    step="0.01"
                    min="0"
                    max="100"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>ค่าธรรมเนียมถอน (%)</label>
                  <input
                    type="number"
                    value={editFormData.payout_fee_percentage}
                    onChange={(e) => setEditFormData({ ...editFormData, payout_fee_percentage: e.target.value })}
                    step="0.01"
                    min="0"
                    max="100"
                    className={INPUT_CLASS}
                  />
                  <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">Payout Fee (บวกเพิ่มจากยอดถอน)</p>
                </div>
              </div>

              <div className="rounded-xl border border-sky-100 dark:border-sky-800 bg-sky-50/40 dark:bg-sky-900/20 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-sky-100 dark:border-sky-800 bg-sky-50/60 dark:bg-sky-900/30">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-sky-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-sky-700 dark:text-sky-300">Telegram</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <span className="text-xs text-gray-400 dark:text-slate-500">{editFormData.telegram_enabled ? 'เปิด' : 'ปิด'}</span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={editFormData.telegram_enabled}
                        onChange={(e) => setEditFormData({ ...editFormData, telegram_enabled: e.target.checked })}
                        className="sr-only"
                      />
                      <div className={`w-9 h-5 rounded-full transition-colors duration-200 ${editFormData.telegram_enabled ? 'bg-sky-500' : 'bg-gray-200 dark:bg-slate-600'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${editFormData.telegram_enabled ? 'translate-x-4' : ''}`} />
                      </div>
                    </div>
                  </label>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className={LABEL_CLASS}>Bot Token</label>
                    <input
                      type="text"
                      value={editFormData.telegram_bot_token}
                      onChange={(e) => setEditFormData({ ...editFormData, telegram_bot_token: e.target.value })}
                      placeholder="1234567890:AAxxxxxxxx..."
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className={LABEL_CLASS}>Chat ID</label>
                    <input
                      type="text"
                      value={editFormData.telegram_chat_id}
                      onChange={(e) => setEditFormData({ ...editFormData, telegram_chat_id: e.target.value })}
                      placeholder="-100xxxxxxxxxx"
                      className={INPUT_CLASS}
                    />
                  </div>
                  <TelegramTopicField
                    wrapperClassName="col-span-2"
                    value={editFormData.telegram_topic_slip_id}
                    onChange={(v) => setEditFormData({ ...editFormData, telegram_topic_slip_id: v })}
                    supported={telegramTopicSupported}
                  />
                </div>
              </div>

              <div className="rounded-xl border border-green-100 dark:border-green-800 overflow-hidden">
                <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20 border-b border-green-100 dark:border-green-800 flex items-center justify-between">
                  <span className="text-sm font-semibold text-green-700 dark:text-green-300">💰 USDT Wallet</span>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className={LABEL_CLASS}>USDT Address</label>
                    <input
                      type="text"
                      value={editFormData.usdt_address}
                      onChange={(e) => setEditFormData({ ...editFormData, usdt_address: e.target.value })}
                      placeholder="TXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      className={`${INPUT_CLASS} font-mono`}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className={LABEL_CLASS}>Network</label>
                    <select
                      value={editFormData.usdt_network}
                      onChange={(e) => setEditFormData({ ...editFormData, usdt_network: e.target.value })}
                      className={INPUT_CLASS}
                    >
                      <option value="TRC20">TRC20 (Tron)</option>
                      <option value="ERC20">ERC20 (Ethereum)</option>
                      <option value="BEP20">BEP20 (BSC)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-purple-100 dark:border-purple-800 overflow-hidden">
                <div className="px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-100 dark:border-purple-800">
                  <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">⚙️ ตั้งค่า Response API (สร้าง Order)</span>
                  <p className="text-xs text-purple-600/80 dark:text-purple-400/80 mt-0.5">เลือกว่า API จะคืนค่า field ใดบ้างเมื่อ merchant สร้าง order</p>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 dark:text-slate-100">ส่ง QR Code (base64)</div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">field <code className="text-purple-600 dark:text-purple-400">qr_code</code> ใน response (data URL ของรูป QR)</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={editFormData.response_qr_enabled}
                        onChange={(e) => setEditFormData({ ...editFormData, response_qr_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className={`w-9 h-5 rounded-full transition-colors duration-200 ${editFormData.response_qr_enabled ? 'bg-purple-500' : 'bg-gray-300 dark:bg-slate-600'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${editFormData.response_qr_enabled ? 'translate-x-4' : ''}`} />
                      </div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 dark:text-slate-100">ส่ง Payment URL</div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">field <code className="text-purple-600 dark:text-purple-400">payment_url</code> ใน response (ลิงก์ <code>https://123pay.asia/pay/...</code>)</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={editFormData.response_payment_url_enabled}
                        onChange={(e) => setEditFormData({ ...editFormData, response_payment_url_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className={`w-9 h-5 rounded-full transition-colors duration-200 ${editFormData.response_payment_url_enabled ? 'bg-purple-500' : 'bg-gray-300 dark:bg-slate-600'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${editFormData.response_payment_url_enabled ? 'translate-x-4' : ''}`} />
                      </div>
                    </label>
                  </div>

                  {/* Warning - both off */}
                  {!editFormData.response_qr_enabled && !editFormData.response_payment_url_enabled && (
                    <div className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <span className="text-red-500 text-base leading-none">⚠️</span>
                      <p className="text-xs text-red-700 dark:text-red-300">ต้องเปิดอย่างน้อย 1 อย่าง ไม่งั้นลูกค้าจะไม่มีทางจ่ายเงิน</p>
                    </div>
                  )}
                </div>
              </div>

              {/* 💧 P2P Deposit Routing (slip-orders → P2P) */}
              <div className="rounded-xl border border-blue-100 dark:border-blue-900/40 overflow-hidden">
                <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/40">
                  <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">💧 P2P Deposit (เส้น slip-orders)</span>
                  <p className="text-xs text-blue-600/80 dark:text-blue-400/80 mt-0.5">เมื่อเปิด: order ที่สร้างผ่าน <code className="text-blue-600 dark:text-blue-400">/api/v1|v2/slip-orders</code> จะ route เข้าระบบ P2P (จับคู่ payout / fallback บัญชีเว็บ) แทน slip ปกติ — ลูกค้าไม่ต้องแก้โค้ด</p>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 dark:text-slate-100">เปิดใช้ P2P Deposit Routing</div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">field <code className="text-blue-600 dark:text-blue-400">bank_info</code> ใน response จะเป็นบัญชีปลายทาง P2P</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={editFormData.p2p_deposit_enabled}
                        onChange={(e) => setEditFormData({ ...editFormData, p2p_deposit_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className={`w-9 h-5 rounded-full transition-colors duration-200 ${editFormData.p2p_deposit_enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-slate-600'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${editFormData.p2p_deposit_enabled ? 'translate-x-4' : ''}`} />
                      </div>
                    </label>
                  </div>

                  {/* 💧 ค่าธรรมเนียม P2P แยกจาก fee ปกติ (ว่าง = fallback) */}
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className={LABEL_CLASS}>ค่าธรรมเนียมฝาก P2P (%)</label>
                      <input
                        type="number"
                        value={editFormData.p2p_fee_payin_pct}
                        onChange={(e) => setEditFormData({ ...editFormData, p2p_fee_payin_pct: e.target.value })}
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder={editFormData.fee_percentage || '-'}
                        className={INPUT_CLASS}
                      />
                      <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">ว่าง = ใช้ค่าธรรมเนียมฝากปกติ ({editFormData.fee_percentage || '-'}%)</p>
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>ค่าธรรมเนียมถอน P2P (%)</label>
                      <input
                        type="number"
                        value={editFormData.p2p_fee_payout_pct}
                        onChange={(e) => setEditFormData({ ...editFormData, p2p_fee_payout_pct: e.target.value })}
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder={editFormData.payout_fee_percentage || '-'}
                        className={INPUT_CLASS}
                      />
                      <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">ว่าง = ใช้ค่าธรรมเนียมถอนปกติ ({editFormData.payout_fee_percentage || '-'}%) — ใช้กับ P2P payout ด้วย</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ⭐ ระดับร้านค้า — endpoint แยก ไม่อยู่ใน payload ของ handleEditSubmit */}
              {tiersSupported && (() => {
                const computedTier = tierDetail?.tier_id != null ? tiersById.get(tierDetail.tier_id) : undefined;
                const effectiveId = tierDetail?.effective?.tier_id ?? null;
                const effectiveTier = effectiveId !== null ? tiersById.get(effectiveId) : undefined;
                const source = tierDetail?.effective?.source ?? 'none';
                const pinnedId = tierDetail?.tier_override_id ?? null;
                const pinnedTier = pinnedId !== null ? tiersById.get(pinnedId) : undefined;
                const options = tiers.filter((t) => t.is_active || t.id === pinnedId);
                const overrideDirty = tierDetail !== null && tierOverrideValue !== (tierDetail.tier_override_id ?? '');
                const history = (tierDetail?.history ?? []).slice(0, 5);
                const reasonLabel: Record<string, string> = {
                  recompute: 'คำนวณใหม่',
                  override_set: 'pin โดยแอดมิน',
                  override_cleared: 'ปลด pin',
                };
                return (
                  <div className="rounded-xl border border-indigo-100 dark:border-indigo-900/40 overflow-hidden">
                    <div className="px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-100 dark:border-indigo-900/40 flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                          <FontAwesomeIcon icon={faLayerGroup} className="mr-1.5" />ระดับร้านค้า
                        </span>
                        <p className="text-xs text-indigo-600/80 dark:text-indigo-400/80 mt-0.5">คำนวณจากยอดสะสมตลอดชีพ (รอบ 01:00) · pin เพื่อกำหนดระดับเองโดยไม่สนยอด</p>
                      </div>
                      {canEditMerchants && (
                        <UiButton
                          variant="secondary"
                          icon={faRotate}
                          loading={tierRecomputing}
                          disabled={tierDetailLoading}
                          onClick={handleRecomputeMerchantTier}
                          className="flex-shrink-0"
                        >
                          คำนวณใหม่
                        </UiButton>
                      )}
                    </div>
                    <div className="p-4 space-y-3">
                      {tierDetailLoading ? (
                        <p className="text-xs text-gray-400 dark:text-slate-500">
                          <FontAwesomeIcon icon={faRotate} className="animate-spin mr-1" />กำลังโหลดข้อมูลระดับ…
                        </p>
                      ) : !tierDetail ? (
                        <p className="text-xs text-gray-400 dark:text-slate-500">ไม่มีข้อมูลระดับของร้านนี้</p>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 break-words">
                            <div className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg min-w-0">
                              <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">ระดับที่คำนวณได้</p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-1.5">
                                {computedTier ? (
                                  <>
                                    <span className="w-2 h-2 rounded-full" style={{ background: computedTier.color || '#9ca3af' }} />
                                    {computedTier.name}
                                  </>
                                ) : tierDetail.tier_id != null ? '(ไม่พบ)' : '-'}
                              </p>
                            </div>
                            <div className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg min-w-0">
                              <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">ระดับที่มีผลจริง</p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-1.5">
                                {effectiveTier ? (
                                  <>
                                    <span className="w-2 h-2 rounded-full" style={{ background: effectiveTier.color || '#9ca3af' }} />
                                    {effectiveTier.name}
                                  </>
                                ) : effectiveId !== null ? '(ไม่พบ)' : '-'}
                                {source === 'override' && <FontAwesomeIcon icon={faThumbtack} className="text-[10px] text-amber-500" title="pin โดยแอดมิน" />}
                              </p>
                              <p className="text-[10px] text-gray-400 dark:text-slate-500">
                                {source === 'override' ? 'จาก pin ของแอดมิน' : source === 'computed' ? 'จากยอดสะสม' : 'ยังไม่ถึงเกณฑ์ระดับใด'}
                              </p>
                            </div>
                            <div className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg min-w-0">
                              <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">order paid สะสม</p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">{fmtNum(tierDetail.tier_paid_orders)} ใบ</p>
                            </div>
                            <div className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg min-w-0">
                              <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">ยอดฝากสะสม</p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">{fmtBaht(tierDetail.tier_paid_amount)}</p>
                            </div>
                          </div>
                          <p className="text-[11px] text-gray-400 dark:text-slate-500">
                            คำนวณล่าสุด: {tierDetail.tier_computed_at ? new Date(tierDetail.tier_computed_at).toLocaleString('th-TH') : 'ยังไม่เคยคำนวณ'}
                          </p>

                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <label className={LABEL_CLASS}>pin ระดับ (override)</label>
                              <select
                                value={tierOverrideValue}
                                onChange={(e) => setTierOverrideValue(e.target.value)}
                                disabled={!canEditMerchants || tierSaving}
                                className={INPUT_CLASS}
                              >
                                <option value="">อัตโนมัติ (ตามยอด)</option>
                                {options.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name} (rank {t.rank}){!t.is_active ? ' (ปิดใช้งาน)' : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {canEditMerchants && (
                              <UiButton
                                icon={faThumbtack}
                                loading={tierSaving}
                                disabled={!overrideDirty}
                                onClick={handleSaveTierOverride}
                                className="flex-shrink-0"
                              >
                                บันทึกระดับ
                              </UiButton>
                            )}
                          </div>
                          {pinnedTier && !pinnedTier.is_active && (
                            <p className="text-[11px] text-red-500 dark:text-red-400">ระดับที่ pin อยู่ถูกปิดใช้งาน — ร้านนี้จะไม่มีระดับที่มีผลจนกว่าจะ pin ใหม่หรือเปลี่ยนเป็นอัตโนมัติ</p>
                          )}

                          {history.length > 0 && (
                            <div className="pt-2 border-t border-gray-100 dark:border-slate-700">
                              <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase mb-1.5">ประวัติล่าสุด</p>
                              <ul className="space-y-1">
                                {history.map((h) => (
                                  <li key={h.id} className="text-[11px] text-gray-600 dark:text-slate-300 flex flex-wrap items-center gap-x-2">
                                    <span className="text-gray-400 dark:text-slate-500 font-mono">{new Date(h.created_at).toLocaleString('th-TH')}</span>
                                    <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-[10px]">{reasonLabel[h.reason] ?? h.reason}</span>
                                    <span>{h.from_tier_name ?? '-'} <FontAwesomeIcon icon={faArrowRight} className="text-[9px] text-gray-400" /> {h.to_tier_name ?? '-'}</span>
                                    {h.actor && <span className="text-gray-400 dark:text-slate-500">โดย {h.actor}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wide mb-2">
                  วิธียืนยันตัวตนเมื่อ Login (2FA)
                </label>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">หากปิด 2FA ระบบจะส่ง Email OTP เสมอ ตัวเลือกนี้ใช้เฉพาะเมื่อเปิด 2FA</p>
                <div className="flex gap-2">
                  {(['totp', 'email'] as const).map((method) => (
                    <label
                      key={method}
                      className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 cursor-pointer transition-all ${
                        editFormData.login_otp_method === method
                          ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 hover:border-gray-300 dark:hover:border-slate-500'
                      }`}
                    >
                      <input
                        type="radio"
                        name="edit_login_otp_method"
                        value={method}
                        checked={editFormData.login_otp_method === method}
                        onChange={() => setEditFormData({ ...editFormData, login_otp_method: method })}
                        className="text-blue-500"
                      />
                      <div>
                        <p className="text-xs font-medium text-gray-800 dark:text-slate-100">
                          {method === 'totp' ? 'Google Auth' : 'Email OTP'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          {method === 'totp' ? 'Authenticator App' : 'ส่งรหัสทางอีเมล'}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
        </form>
      </UiModal>
      {assignTarget && (
        <UiModal
          open={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          title="ย้ายเข้า Reseller"
          icon={faHandshake}
          iconColor="text-violet-500"
          size="md"
          footer={
            <div className="flex gap-3">
              <UiButton variant="secondary" onClick={() => setShowAssignModal(false)} className="flex-1 justify-center">
                ยกเลิก
              </UiButton>
              <UiButton
                onClick={handleAssign}
                loading={assignLoading}
                disabled={!selectedResellerId || !!feeWarning}
                className="flex-1 justify-center"
              >
                ยืนยันย้าย
              </UiButton>
            </div>
          }
        >
          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3.5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                 style={{ background: 'var(--theme-primary-gradient)' }}>
              {assignTarget.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 dark:text-slate-100 text-sm truncate">{assignTarget.name}</p>
              <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{assignTarget.email}</p>
            </div>
            <div className="ml-auto text-right flex-shrink-0">
              <p className="text-[10px] text-gray-400 dark:text-slate-500">Fee ปัจจุบัน</p>
              <p className="text-sm font-bold text-gray-700 dark:text-slate-200">{assignTarget.fee_percentage}%</p>
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>เลือก Reseller</label>
            <select
              value={selectedResellerId}
              onChange={(e) => setSelectedResellerId(e.target.value)}
              className={INPUT_CLASS}
            >
              <option value="">-- เลือก Reseller --</option>
              {resellersList.filter(r => r.is_active).map(r => (
                <option key={r.id} value={r.id}>
                  {r.name} (Rate: {r.fee_rate}% | Merchants: {r.merchant_count})
                </option>
              ))}
            </select>
          </div>

          {selectedReseller && (
            <>
              <div>
                <label className={LABEL_CLASS}>
                  Fee Merchant (%)
                  <span className="text-gray-400 dark:text-slate-500 normal-case font-normal ml-1">ต้อง &ge; {selectedReseller.fee_rate}%</span>
                </label>
                <input
                  type="number"
                  value={assignFee}
                  onChange={(e) => setAssignFee(e.target.value)}
                  step="0.01"
                  min={selectedReseller.fee_rate}
                  max="100"
                  className={`w-full px-3.5 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 dark:text-slate-200
                    ${feeWarning
                      ? 'border-red-300 dark:border-red-700 focus:ring-red-500/20 focus:border-red-400 bg-red-50 dark:bg-red-900/20'
                      : `${INPUT_CLASS}`
                    }`}
                />
              </div>

              {feeWarning && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3.5">
                  <p className="text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Fee ต่ำกว่า Reseller Rate!</strong> Merchant fee ({assignFeeNum}%) น้อยกว่า Reseller rate ({selectedReseller.fee_rate}%)
                      กรุณาปรับ fee ให้ &ge; {selectedReseller.fee_rate}%
                    </span>
                  </p>
                </div>
              )}

              {!feeWarning && (
                <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl p-3.5">
                  <p className="text-xs font-semibold text-violet-700 dark:text-violet-300 mb-2">Commission Preview</p>
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 dark:text-slate-500">Merchant Fee</p>
                      <p className="font-bold text-gray-700 dark:text-slate-200">{assignFeeNum}%</p>
                    </div>
                    <span className="text-gray-300 dark:text-slate-600">-</span>
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 dark:text-slate-500">Reseller Rate</p>
                      <p className="font-bold text-gray-700 dark:text-slate-200">{selectedReseller.fee_rate}%</p>
                    </div>
                    <FontAwesomeIcon icon={faArrowRight} className="text-violet-400 text-xs mx-1" />
                    <div className="text-center">
                      <p className="text-[10px] text-violet-500 dark:text-violet-400 font-semibold">Commission</p>
                      <p className="font-bold text-violet-600 dark:text-violet-400">{commissionPreview.toFixed(2)}%</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400 dark:text-slate-500 text-center mt-2">
                    ทุก ฿1,000 Reseller ได้ ฿{(1000 * commissionPreview / 100).toFixed(2)}
                  </p>
                </div>
              )}
            </>
          )}
        </UiModal>
      )}
    </div>
  );
};

export default MerchantsManagementPage;
